import React, { useState, useEffect } from 'react';
import {
  Target,
  Search,
  Filter,
  Eye,
  Phone,
  Mail,
  Calendar,
  Star,
  TrendingUp,
  User,
  RefreshCw,
  MapPin,
  GraduationCap,
  BookOpen,
  Euro
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import LeadDetailModal from '../Modals/LeadDetailModal';

interface Lead {
  id: string;
  first_name: string;
  last_name: string;
  emails: string[];
  phone: string;
  status: string;
  priority: string;
  interested_services: string[];
  interested_programs: string[];
  interested_universities: string[];
  education_level: string;
  english_level: string;
  italian_level: string;
  notes: string;
  nationality: string;
  contact_role: string;
  created_at: string;
  created_by: string;
  follow_up_date: string;
  follow_up_notes: string;
  guardian_info: any;
  creator?: {
    name: string;
  };
  contracts?: {
    id: string;
    contract_number: string;
    total_amount: number;
    currency: string;
    transferred_to_operations_at: string;
  }[];
}

const OperationsLeads: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [profiles, setProfiles] = useState<any[]>([]);

  useEffect(() => {
    fetchTransferredLeads();
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('id, name')
        .eq('branch_id', user?.branchId)
        .eq('is_active', true);

      setProfiles(data || []);
    } catch (err) {
      console.error('Error fetching profiles:', err);
    }
  };

  const fetchTransferredLeads = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('contracts')
        .select('student_id, id, contract_number, total_amount, currency, transferred_to_operations_at')
        .eq('transferred_to_operations', true);

      if (user?.branchId && user?.role !== 'admin') {
        query = query.eq('branch_id', user.branchId);
      }

      const { data: contractsData, error: contractsError } = await query.order('transferred_to_operations_at', { ascending: false });

      if (contractsError) throw contractsError;

      if (contractsData && contractsData.length > 0) {
        const { data: studentsData, error: studentsError } = await supabase
          .from('students')
          .select('id, email')
          .in('id', contractsData.map(c => c.student_id));

        if (studentsError) throw studentsError;

        const studentEmailMap = new Map(studentsData?.map(s => [s.id, s.email]) || []);
        const studentEmails = Array.from(studentEmailMap.values()).filter(Boolean);

        if (studentEmails.length > 0) {
          const { data: leadsData, error: leadsError } = await supabase
            .from('leads')
            .select(`
              *,
              creator:profiles!leads_created_by_fkey(name)
            `)
            .eq('branch_id', user?.branchId)
            .or(studentEmails.map(email => `emails.cs.{${email}}`).join(','))
            .order('created_at', { ascending: false });

          if (leadsError) throw leadsError;

          const leadsWithContracts = (leadsData || []).map(lead => {
            const matchingStudents = Array.from(studentEmailMap.entries())
              .filter(([_, email]) => lead.emails?.includes(email))
              .map(([studentId]) => studentId);

            const contracts = contractsData.filter(c =>
              matchingStudents.includes(c.student_id)
            );

            return {
              ...lead,
              contracts
            };
          }).filter(lead => lead.contracts && lead.contracts.length > 0);

          setLeads(leadsWithContracts);
        } else {
          setLeads([]);
        }
      } else {
        setLeads([]);
      }
    } catch (err) {
      console.error('Error fetching transferred leads:', err);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLead = async (leadId: string, updates: any) => {
    try {
      const { error } = await supabase
        .from('leads')
        .update(updates)
        .eq('id', leadId);

      if (error) throw error;
      fetchTransferredLeads();
    } catch (error) {
      console.error('Lead güncellenirken hata oluştu:', error);
      alert('Güncelleme sırasında bir hata oluştu.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new_lead': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
      case 'contacted': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'qualified': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'unqualified': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'converted': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'called': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      case 'meeting_scheduled': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'proposal_sent': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'new_lead': return 'Yeni Lead';
      case 'contacted': return 'İletişim Kuruldu';
      case 'qualified': return 'Nitelikli';
      case 'unqualified': return 'Niteliksiz';
      case 'converted': return 'Dönüştürüldü';
      case 'called': return 'Arandı';
      case 'no_answer': return 'Cevapsız';
      case 'busy': return 'Meşgul';
      case 'meeting_scheduled': return 'Görüşme Sağlandı';
      case 'proposal_sent': return 'Teklif Gönderildi';
      default: return status;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const filteredLeads = leads.filter(lead => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      lead.first_name.toLowerCase().includes(searchLower) ||
      lead.last_name.toLowerCase().includes(searchLower) ||
      lead.emails?.some(e => e.toLowerCase().includes(searchLower)) ||
      lead.phone?.toLowerCase().includes(searchLower);

    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || lead.priority === priorityFilter;

    return matchesSearch && matchesStatus && matchesPriority;
  });

  const stats = {
    totalLeads: leads.length,
    contactedLeads: leads.filter(l => l.status === 'contacted' || l.status === 'called').length,
    highPriorityLeads: leads.filter(l => l.priority === 'high').length,
    scheduledMeetings: leads.filter(l => l.status === 'meeting_scheduled').length,
  };

  return (
    <div className="p-8 space-y-8 bg-gray-50/50 dark:bg-gray-950 min-h-screen font-sans">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-extrabold text-gray-800 dark:text-gray-100 tracking-tight">
            Muhasebeden Gelenler
            <span className="text-blue-500">.</span>
          </h1>
          <div className="flex items-center gap-2 mt-2 text-gray-500 dark:text-gray-400 font-medium">
            <User className="w-4 h-4" />
            <span>Muhasebeye gönderilen {filteredLeads.length} lead</span>
          </div>
        </div>
        <button
          onClick={fetchTransferredLeads}
          disabled={loading}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-semibold transition-all shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Yenile</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Total Leads Card */}
        <div className="relative overflow-hidden group bg-white dark:bg-gray-800 rounded-3xl p-6 border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:shadow-blue-100/50 dark:hover:shadow-blue-900/20 transition-all duration-500">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 dark:bg-blue-900/20 rounded-full blur-3xl -mr-16 -mt-16 transition-all group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform duration-500">
                <Target className="w-6 h-6" />
              </div>
              <span className="text-blue-600 dark:text-blue-400 text-xs font-bold bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full">
                Toplam
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-3xl font-black text-gray-800 dark:text-white tracking-tight">
                {stats.totalLeads}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                Kayıtlı Lead
              </p>
            </div>
          </div>
        </div>

        {/* Contacted Card */}
        <div className="relative overflow-hidden group bg-white dark:bg-gray-800 rounded-3xl p-6 border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:shadow-emerald-100/50 dark:hover:shadow-emerald-900/20 transition-all duration-500">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 dark:bg-emerald-900/20 rounded-full blur-3xl -mr-16 -mt-16 transition-all group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/30"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/30 rounded-2xl flex items-center justify-center text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform duration-500">
                <Phone className="w-6 h-6" />
              </div>
              <span className="text-emerald-600 dark:text-emerald-400 text-xs font-bold bg-emerald-50 dark:bg-emerald-900/30 px-3 py-1 rounded-full">
                İletişim
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-3xl font-black text-gray-800 dark:text-white tracking-tight">
                {stats.contactedLeads}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                Kurulan
              </p>
            </div>
          </div>
        </div>

        {/* High Priority Card */}
        <div className="relative overflow-hidden group bg-white dark:bg-gray-800 rounded-3xl p-6 border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:shadow-amber-100/50 dark:hover:shadow-amber-900/20 transition-all duration-500">
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-50 dark:bg-amber-900/20 rounded-full blur-3xl -mr-16 -mt-16 transition-all group-hover:bg-amber-100 dark:group-hover:bg-amber-900/30"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-amber-50 dark:bg-amber-900/30 rounded-2xl flex items-center justify-center text-amber-600 dark:text-amber-400 group-hover:scale-110 transition-transform duration-500">
                <Star className="w-6 h-6" />
              </div>
              <span className="text-amber-600 dark:text-amber-400 text-xs font-bold bg-amber-50 dark:bg-amber-900/30 px-3 py-1 rounded-full">
                Öncelik
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-3xl font-black text-gray-800 dark:text-white tracking-tight">
                {stats.highPriorityLeads}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                Yüksek
              </p>
            </div>
          </div>
        </div>

        {/* Scheduled Card */}
        <div className="relative overflow-hidden group bg-white dark:bg-gray-800 rounded-3xl p-6 border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:shadow-violet-100/50 dark:hover:shadow-violet-900/20 transition-all duration-500">
          <div className="absolute top-0 right-0 w-32 h-32 bg-violet-50 dark:bg-violet-900/20 rounded-full blur-3xl -mr-16 -mt-16 transition-all group-hover:bg-violet-100 dark:group-hover:bg-violet-900/30"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-violet-50 dark:bg-violet-900/30 rounded-2xl flex items-center justify-center text-violet-600 dark:text-violet-400 group-hover:scale-110 transition-transform duration-500">
                <Calendar className="w-6 h-6" />
              </div>
              <span className="text-violet-600 dark:text-violet-400 text-xs font-bold bg-violet-50 dark:bg-violet-900/30 px-3 py-1 rounded-full">
                Planlı
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-3xl font-black text-gray-800 dark:text-white tracking-tight">
                {stats.scheduledMeetings}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                Görüşme
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Lead ara (İsim, email, telefon)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50/50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
            />
          </div>

          <label className="relative block">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-4 pr-10 py-3 appearance-none bg-gray-50/50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
            >
              <option value="all">Tüm Durumlar</option>
              <option value="new_lead">Yeni Lead</option>
              <option value="called">Arandı</option>
              <option value="no_answer">Cevapsız</option>
              <option value="meeting_scheduled">Görüşme Sağlandı</option>
              <option value="proposal_sent">Teklif Gönderildi</option>
            </select>
            <Filter className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4" />
          </label>

          <label className="relative block">
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="w-full pl-4 pr-10 py-3 appearance-none bg-gray-50/50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
            >
              <option value="all">Tüm Öncelikler</option>
              <option value="high">Yüksek</option>
              <option value="medium">Orta</option>
              <option value="low">Düşük</option>
            </select>
            <Star className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4" />
          </label>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            <span className="ml-4 text-gray-500 dark:text-gray-400 font-medium">Yükleniyor...</span>
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-gray-50 dark:bg-gray-900/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Target className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Lead bulunamadı</h3>
            <p className="text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
              Arama kriterlerinize uygun veya henüz atanmış bir lead bulunmuyor.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700">
                  <th className="text-left py-4 px-6 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Lead Bilgisi</th>
                  <th className="text-left py-4 px-6 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">İletişim</th>
                  <th className="text-left py-4 px-6 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Durum</th>
                  <th className="text-left py-4 px-6 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Öncelik</th>
                  <th className="text-left py-4 px-6 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sözleşme</th>
                  <th className="text-left py-4 px-6 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">İlgilendiği Program</th>
                  <th className="text-left py-4 px-6 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Düzey</th>
                  <th className="text-left py-4 px-6 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tarih</th>
                  <th className="text-right py-4 px-6 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filteredLeads.map((lead) => (
                  <tr key={lead.id} className="group hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-full flex items-center justify-center text-blue-700 dark:text-blue-300 font-bold text-sm">
                          {(lead.first_name?.[0] || '') + (lead.last_name?.[0] || '')}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {lead.first_name} {lead.last_name}
                          </p>
                          <div className="flex items-center mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${lead.contact_role === 'student'
                              ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                              : 'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
                              }`}>
                              {lead.contact_role === 'student' ? 'Öğrenci' : 'Veli'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="space-y-1.5">
                        {lead.emails && lead.emails.length > 0 && (
                          <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                            <Mail className="w-3.5 h-3.5" />
                            <span className="truncate max-w-[180px]">{lead.emails[0]}</span>
                          </div>
                        )}
                        {lead.phone && (
                          <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                            <Phone className="w-3.5 h-3.5" />
                            <span>{lead.phone}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(lead.status)}`}>
                        {getStatusText(lead.status)}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getPriorityColor(lead.priority)}`}>
                        {lead.priority === 'high' ? 'Yüksek' : lead.priority === 'medium' ? 'Orta' : 'Düşük'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      {lead.contracts && lead.contracts.length > 0 ? (
                        <div className="text-sm">
                          <p className="font-semibold text-gray-900 dark:text-white font-mono">
                            {lead.contracts[0].contract_number}
                          </p>
                          <p className="text-emerald-600 dark:text-emerald-400 font-medium text-xs mt-0.5">
                            {lead.contracts[0].currency} {lead.contracts[0].total_amount.toLocaleString()}
                          </p>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-sm text-gray-600 dark:text-gray-400 max-w-[200px]">
                        {lead.interested_programs && lead.interested_programs.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {lead.interested_programs.slice(0, 2).map((program, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs">
                                {program}
                              </span>
                            ))}
                            {lead.interested_programs.length > 2 && (
                              <span className="text-xs text-gray-400 pl-1">+{lead.interested_programs.length - 2}</span>
                            )}
                          </div>
                        ) : '-'}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
                        {lead.education_level === 'bachelor' ? 'Lisans' :
                          lead.education_level === 'master' ? 'Yüksek Lisans' :
                            lead.education_level || '-'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      {lead.contracts && lead.contracts.length > 0 && (
                        <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                          <Calendar className="w-3.5 h-3.5" />
                          {format(new Date(lead.contracts[0].transferred_to_operations_at), 'd MMM yyyy', { locale: tr })}
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <button
                        onClick={() => {
                          setSelectedLead(lead);
                          setShowDetailModal(true);
                        }}
                        className="inline-flex items-center justify-center p-2.5 text-blue-600 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 rounded-xl transition-all"
                        title="Detayları ve Dosyayı Gör"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showDetailModal && selectedLead && (
        <LeadDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedLead(null);
          }}
          lead={selectedLead}
          onEdit={() => { console.log('Edit clicked'); }}
          onUpdateLead={handleUpdateLead}
        />
      )}
    </div>
  );
};

export default OperationsLeads;
